// Copyright 2021 Pangea Cyber Corporation
// Author: Pangea Cyber Corporation

import { createTheme } from "@mui/material/styles";
import { Theme, ThemeOptions } from "@mui/system";
import { deepmerge } from "@mui/utils";

import { Colors } from "./colors";
import { Colors as ColorsTwentyFive } from "./colors-twentyfive";
import { Colors as ColorsTwentyFiveDark } from "./colors-twentyfive-dark";
import { Colors as ColorsCs } from "./colors-cs";
import components, { AllComponents } from "./components";
import themePalette from "./palette";
import { createBreakpoints } from "@mui/system";
import { uiContainerTransitionMs, uiOpacityHover } from "@src/utils";

const themeFonts = () => {
  return {
    fontFamily: ["Kanit", "sans-serif"].join(","),
  };
};

export const getTheme = (
  options: { themeOverride?: ThemeOptions } = {},
): Theme => {
  const theme_ = createTheme(
    deepmerge(
      {
        palette: themePalette(Colors),
        components: components(Colors),
        typography: themeFonts(),
      },
      options.themeOverride,
    ),
  );

  return theme_;
};

const breakpoints = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1236,
      ll: 1320,
      xl: 1536,
    },
  },
};
const theme = getTheme({ themeOverride: { ...breakpoints } });

export default theme;

export const getTwentyFourTheme = (theme: any) => {
  const colorsToUse = Colors;

  return createTheme({
    ...theme,
    palette: themePalette(Colors),
    breakpoints: createBreakpoints({
      keys: ["xs", "sm", "md", "lg", "xl"],
      values: {
        xs: 0,
        sm: 600,
        md: 1024,
        lg: 1280,
        xl: 1440,
      },
    }),
    typography: {
      ...theme.typography,
      fontFamily: "Inter",
      allVariants: {
        ...theme.typography.allVariants,
        fontFamily: "Inter",
      },
    },
    components: {
      MuiButton: {
        ...theme.components.MuiButton,
        defaultProps: {
          disableElevation: true,
          disableFocusRipple: true,
          disableRipple: true,
          disableTouchRipple: true,
        },
        variants: [
          {
            props: { variant: "containedInverse" },
            style: {
              ...theme.components?.MuiButton?.styleOverrides?.contained,
              color: colorsToUse.PrimaryMain,
              backgroundColor: colorsToUse.PrimaryContrast,
              borderRadius: "8px",
              fontWeight: 400,
              fontSize: "16px",
              fontFamily: "Inter",
              padding: "16px 32px",
            },
          },
        ],
        styleOverrides: {
          ...theme.components?.MuiButton?.styleOverrides,
          fontFamily: "Inter",
          root: {
            ...theme.components?.MuiButton?.styleOverrides?.root,
            color: colorsToUse.PrimaryContrast,
            borderRadius: "8px",
            fontFamily: "Inter",
            fontWeight: 400,
            fontSize: "16px",
            padding: "16px 32px",
          },
          contained: {
            ...theme.components?.MuiButton?.styleOverrides?.contained,
            color: colorsToUse.PrimaryContrast,
            borderRadius: "8px",
            fontFamily: "Inter",
            fontWeight: 400,
            fontSize: "16px",
            padding: "16px 32px",
          },
        },
      },
      MuiTypography: {
        ...theme.components.MuiTypography,
        defaultProps: {
          ...theme.components.MuiTypography.defaultProps,
        },
        styleOverrides: {
          ...theme.components.MuiTypography.styleOverrides,
          h1: {
            ...theme.components.MuiTypography.h1,
            fontFamily: "Kanit",
            fontWeight: 500,
            fontSize: "70px",
            letterSpacing: "-1.75px",
            lineHeight: "150%",
            color: colorsToUse.TextPrimary,
          },
          h2: {
            ...theme.components.MuiTypography.h2,
            fontFamily: "Kanit",
            fontWeight: 600,
            lineHeight: "50px",
            fontSize: "48px",
            color: colorsToUse.TextPrimary,
          },
          h3: {
            ...theme.components.MuiTypography.h3,
            fontFamily: "Kanit",
            fontSize: "44px",
            lineHeight: "46px",
            [theme.breakpoints.up("sm")]: {
              fontSize: "48px",
              lineHeight: "50px",
            },
            color: colorsToUse.TextPrimary,
          },
          h4: {
            ...theme.components.MuiTypography.h4,
            fontFamily: "Kanit",
            fontSize: "19px",
            lineHeight: "150%",
            color: colorsToUse.TextPrimary,
            fontWeight: 500,
          },
          h5: {
            ...theme.components.MuiTypography.h5,
            fontFamily: "Kanit",
            lineHeight: "150%",
            color: colorsToUse.TextPrimary,
          },
          h6: {
            ...theme.components.MuiTypography.h6,
            fontFamily: "Kanit",
            lineHeight: "150%",
            color: colorsToUse.TextPrimary,
          },
          subtitle1: {
            ...theme.components.MuiTypography.subtitle1,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "1rem",
            lineHeight: "1.5rem",
            letterSpacing: 0,
          },
          subtitle2: {
            ...theme.components.MuiTypography.subtitle2,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "0.875rem",
            lineHeight: "1.375rem",
            letterSpacing: 0,
          },
          body1: {
            ...theme.components.MuiTypography.body1,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: "18px",
            [theme.breakpoints.down("md")]: {
              fontSize: "16px",
            },
            lineHeight: "175%",
            letterSpacing: 0,
            color: colorsToUse.BodyText,
          },
          body2: {
            ...theme.components.MuiTypography.body2,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: "16px",
            lineHeight: "28px",
            letterSpacing: 0,
            color: colorsToUse.BodyText,
          },
          caption: {
            ...theme.components.MuiTypography.caption,
            fontFamily: "Inter",
            textTransform: "uppercase",
            color: colorsToUse.HomeBodyText,
            fontSize: "12px",
            [theme.breakpoints.up("md")]: {
              fontSize: "14px",
            },
            fontWeight: 500,
            letterSpacing: 2.8,
          },
          button: {
            ...theme.components.MuiTypography.button,
            color: colorsToUse.PrimaryContrast,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "200%",
            textTransform: "capitalize",
          },
          overline: {
            ...theme.components.MuiTypography.overline,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "0.75rem",
            lineHeight: "1.25rem",
            letterSpacing: "0.0625rem",
            textTransform: "uppercase",
          },
        },
      },
    },
  });
};

export const getTwentyFiveTheme = (theme: any) => {
  const colorsToUse = ColorsTwentyFive;

  return createTheme({
    ...theme,
    palette: themePalette(ColorsTwentyFive),
    breakpoints: createBreakpoints({
      keys: ["xs", "sm", "md", "lg", "xl"],
      values: {
        xs: 0,
        sm: 600,
        md: 1024,
        lg: 1280,
        xl: 1440,
      },
    }),
    typography: {
      ...theme.typography,
      fontFamily: "Inter",
      allVariants: {
        ...theme.typography.allVariants,
        fontFamily: "Inter",
      },
    },
    components: {
      MuiTooltip: {
        ...theme.components.MuiTooltip,
        styleOverrides: {
          tooltip: {
            backgroundColor: `rgba(0, 0, 0, 0.87)`,
            color: colorsToUse.PrimaryContrast,
          },
        },
      },
      MuiButton: {
        ...theme.components.MuiButton,
        defaultProps: {
          disableElevation: true,
          disableFocusRipple: true,
          disableRipple: true,
          disableTouchRipple: true,
        },
        variants: [
          {
            props: { variant: "containedInverse" },
            style: {
              ...theme.components?.MuiButton?.styleOverrides?.contained,
              color: colorsToUse.PrimaryMain,
              backgroundColor: colorsToUse.PrimaryContrast,
              borderRadius: "8px",
              fontWeight: 400,
              fontSize: "15px",
              fontFamily: "Inter",
              padding: "16px 32px",
            },
          },
        ],
        styleOverrides: {
          ...theme.components?.MuiButton?.styleOverrides,
          fontFamily: "Inter",
          root: {
            ...theme.components?.MuiButton?.styleOverrides?.root,
            color: colorsToUse.PrimaryContrast,
            borderRadius: "8px",
            fontFamily: "Inter",
            fontWeight: 400,
            paddingLeft: "32px",
            paddingRight: "32px",
            fontSize: "20px",
            opacity: 1,
            transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
            "&:hover": {
              opacity: uiOpacityHover,
              color: colorsToUse.TextPrimary,
              textDecoration: "none",
            },
          },
          contained: {
            ...theme.components?.MuiButton?.styleOverrides?.contained,
            color: colorsToUse.TextPrimary,
            background: colorsToUse.PrimaryGradient,
            borderRadius: "8px",
            fontFamily: "Inter",
            fontWeight: 500,
            paddingLeft: "32px",
            paddingRight: "32px",
          },
          outlined: {
            ...theme.components?.MuiButton?.styleOverrides?.outlined,
            color: colorsToUse.TextPrimary,
            backgroundColor: "transparent",
            boxShadow: `inset 0px 0px 0px 1px ${colorsToUse.TextPrimary}`,
            borderRadius: "8px",
            fontFamily: "Inter",
            fontWeight: 400,
            paddingLeft: "32px",
            paddingRight: "32px",
            "&:hover": {
              boxShadow: `inset 0px 0px 0px 1px ${colorsToUse.TextPrimary}`,
            },
          },
        },
      },
      MuiLink: {
        ...theme.components.MuiLink,
        styleOverrides: {
          root: {
            color: colorsToUse.TextPrimary,
            fontFamily: "Inter",
            opacity: 1,
            transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
            "&:hover": {
              opacity: uiOpacityHover,
              color: colorsToUse.TextPrimary,
              textDecoration: "none",
            },
          },
        },
      },
      MuiTypography: {
        ...theme.components.MuiTypography,
        styleOverrides: {
          ...theme.components.MuiTypography.styleOverrides,
          h1: {
            ...theme.components.MuiTypography.h1,
            fontFamily: "Inter",
            fontWeight: 800,
            letterSpacing: "-1.75px",
            fontSize: "70px",
            lineHeight: "72px",
            [theme.breakpoints.down("md")]: {
              fontSize: "64px",
              lineHeight: "66px",
            },
            [theme.breakpoints.down("sm")]: {
              fontSize: "56px",
              lineHeight: "58px",
            },
            color: colorsToUse.TextPrimary,
          },
          h2: {
            ...theme.components.MuiTypography.h2,
            fontFamily: "Inter",
            fontWeight: 800,
            letterSpacing: "-1.75px",
            lineHeight: "50px",
            fontSize: "48px",
            [theme.breakpoints.down("md")]: {
              fontSize: "42px",
              lineHeight: "44px",
            },
            [theme.breakpoints.down("sm")]: {
              fontSize: "38px",
              lineHeight: "40px",
            },
            color: colorsToUse.TextPrimary,
          },
          h3: {
            ...theme.components.MuiTypography.h3,
            fontFamily: "Inter",
            letterSpacing: "-1.75px",
            fontSize: "44px",
            lineHeight: "46px",
            [theme.breakpoints.down("md")]: {
              fontSize: "36px",
              lineHeight: "38px",
            },
            [theme.breakpoints.down("sm")]: {
              fontSize: "32px",
              lineHeight: "34px",
            },
            color: colorsToUse.TextPrimary,
          },
          h4: {
            ...theme.components.MuiTypography.h4,
            fontFamily: "Inter",
            fontSize: "26px",
            letterSpacing: "-1.75px",
            lineHeight: "150%",
            color: colorsToUse.InfoLight,
            fontWeight: 800,
          },
          h5: {
            ...theme.components.MuiTypography.h5,
            fontFamily: "Inter",
            letterSpacing: "-1.75px",
            lineHeight: "150%",
            color: colorsToUse.TextPrimary,
          },
          h6: {
            ...theme.components.MuiTypography.h6,
            fontFamily: "Inter",
            letterSpacing: "-1.75px",
            lineHeight: "150%",
            color: colorsToUse.TextPrimary,
          },
          subtitle1: {
            ...theme.components.MuiTypography.subtitle1,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "1rem",
            lineHeight: "1.5rem",
            letterSpacing: 0,
          },
          subtitle2: {
            ...theme.components.MuiTypography.subtitle2,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "0.875rem",
            lineHeight: "1.375rem",
            letterSpacing: 0,
          },
          body1: {
            ...theme.components.MuiTypography.body1,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: "20px",
            [theme.breakpoints.down("md")]: {
              fontSize: "16px",
            },
            lineHeight: "32px",
            letterSpacing: 0,
            color: colorsToUse.BodyText,
          },
          body2: {
            ...theme.components.MuiTypography.body2,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: "18px",
            lineHeight: "150%",
            letterSpacing: 0,
            color: colorsToUse.BodyText,
          },
          caption: {
            ...theme.components.MuiTypography.caption,
            fontFamily: "Inter",
            textTransform: "uppercase",
            color: colorsToUse.TextPrimary,
            fontSize: "12px",
            [theme.breakpoints.up("md")]: {
              fontSize: "14px",
            },
            fontWeight: 500,
            letterSpacing: 2.8,
          },
          button: {
            ...theme.components.MuiTypography.button,
            color: colorsToUse.PrimaryContrast,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "200%",
            textTransform: "capitalize",
          },
          overline: {
            ...theme.components.MuiTypography.overline,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "0.75rem",
            lineHeight: "1.25rem",
            letterSpacing: "0.0625rem",
            textTransform: "uppercase",
          },
        },
      },
    },
  });
};

export const getTwentyFiveDarkTheme = (theme: any) => {
  const colorsToUse = ColorsTwentyFiveDark;

  return createTheme({
    ...theme,
    palette: themePalette(ColorsTwentyFiveDark),
    breakpoints: createBreakpoints({
      keys: ["xs", "sm", "md", "lg", "xl"],
      values: {
        xs: 0,
        sm: 600,
        md: 1024,
        lg: 1280,
        xl: 1440,
      },
    }),
    typography: {
      ...theme.typography,
      fontFamily: "Inter",
      allVariants: {
        ...theme.typography.allVariants,
        fontFamily: "Inter",
      },
    },
    components: {
      MuiButton: {
        ...theme.components.MuiButton,
        defaultProps: {
          disableElevation: true,
          disableFocusRipple: true,
          disableRipple: true,
          disableTouchRipple: true,
        },
        variants: [
          {
            props: { variant: "containedInverse" },
            style: {
              ...theme.components?.MuiButton?.styleOverrides?.contained,
              color: colorsToUse.PrimaryMain,
              backgroundColor: colorsToUse.PrimaryContrast,
              borderRadius: "8px",
              fontWeight: 400,
              fontSize: "16px",
              fontFamily: "Inter",
              padding: "16px 32px",
            },
          },
        ],
        styleOverrides: {
          ...theme.components?.MuiButton?.styleOverrides,
          fontFamily: "Inter",
          root: {
            ...theme.components?.MuiButton?.styleOverrides?.root,
            color: colorsToUse.PrimaryContrast,
            borderRadius: "8px",
            fontFamily: "Inter",
            fontWeight: 400,
            fontSize: "16px",
            padding: "16px 32px",
          },
          contained: {
            ...theme.components?.MuiButton?.styleOverrides?.contained,
            color: colorsToUse.PrimaryContrast,
            borderRadius: "8px",
            fontFamily: "Inter",
            fontWeight: 400,
            fontSize: "16px",
            padding: "16px 32px",
          },
        },
      },
      MuiTypography: {
        ...theme.components.MuiTypography,
        defaultProps: {
          ...theme.components.MuiTypography.defaultProps,
        },
        styleOverrides: {
          ...theme.components.MuiTypography.styleOverrides,
          h1: {
            ...theme.components.MuiTypography.h1,
            fontFamily: "Inter",
            fontWeight: 400,
            fontSize: "60px",
            letterSpacing: "0.2px",
            lineHeight: "73px",
            color: colorsToUse.TextPrimary,
          },
          h2: {
            ...theme.components.MuiTypography.h2,
            fontFamily: "Inter",
            fontWeight: 400,
            lineHeight: "140%",
            fontSize: "48px",
            [theme.breakpoints.down("md")]: {
              fontSize: "36px",
            },
            color: colorsToUse.TextPrimary,
          },
          h3: {
            ...theme.components.MuiTypography.h3,
            fontFamily: "Inter",
            fontSize: "48px",
            lineHeight: "50px",
            [theme.breakpoints.down("md")]: {
              fontSize: "28px",
              lineHeight: "30px",
            },
            color: colorsToUse.TextPrimary,
          },
          h4: {
            ...theme.components.MuiTypography.h4,
            fontFamily: "Inter",
            fontSize: "20px",
            lineHeight: "140%",
            color: colorsToUse.TextPrimary,
            fontWeight: 400,
          },
          h5: {
            ...theme.components.MuiTypography.h5,
            fontFamily: "Inter",
            lineHeight: "150%",
            color: colorsToUse.TextPrimary,
          },
          h6: {
            ...theme.components.MuiTypography.h6,
            fontFamily: "Inter",
            lineHeight: "150%",
            color: colorsToUse.TextPrimary,
          },
          subtitle1: {
            ...theme.components.MuiTypography.subtitle1,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "1rem",
            lineHeight: "1.5rem",
            letterSpacing: 0,
          },
          subtitle2: {
            ...theme.components.MuiTypography.subtitle2,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "0.875rem",
            lineHeight: "1.375rem",
            letterSpacing: 0,
          },
          body1: {
            ...theme.components.MuiTypography.body1,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: "18px",
            // [theme.breakpoints.down("md")]: {
            //   fontSize: "16px",
            // },
            lineHeight: "175%",
            letterSpacing: 0,
            color: colorsToUse.BodyText,
          },
          body2: {
            ...theme.components.MuiTypography.body2,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 300,
            fontSize: "16px",
            lineHeight: "25px",
            letterSpacing: "0.2px",
            color: colorsToUse.BodyText,
          },
          caption: {
            ...theme.components.MuiTypography.caption,
            fontFamily: "Inter",
            textTransform: "uppercase",
            color: colorsToUse.InfoMain,
            fontSize: "16px",
            // [theme.breakpoints.up("md")]: {
            //   fontSize: "14px",
            // },
            fontWeight: 500,
            letterSpacing: "0%",
          },
          button: {
            ...theme.components.MuiTypography.button,
            color: colorsToUse.PrimaryContrast,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "200%",
            textTransform: "capitalize",
          },
          overline: {
            ...theme.components.MuiTypography.overline,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "0.75rem",
            lineHeight: "1.25rem",
            letterSpacing: "0.0625rem",
            textTransform: "uppercase",
          },
        },
      },
      MuiLink: {
        ...theme.components.MuiLink,
        styleOverrides: {
          root: {
            fontFamily: "Inter",
          },
        },
      },
    },
  });
};

export const getCSTheme = (theme: any) => {
  const colorsToUse = ColorsCs;

  return createTheme({
    ...theme,
    palette: themePalette(ColorsCs),
    breakpoints: createBreakpoints({
      keys: ["xs", "sm", "md", "lg", "xl"],
      values: {
        xs: 0,
        sm: 600,
        md: 1024,
        lg: 1280,
        xl: 1440,
      },
    }),
    typography: {
      ...theme.typography,
      fontFamily: "Neue Haas Grotesk Display Pro",
      root: {
        ...theme.typography.root,
        fontFamily: "Neue Haas Grotesk Display Pro",
      },
      allVariants: {
        ...theme.typography.allVariants,
        fontFamily: "Neue Haas Grotesk Display Pro",
      },
    },
    components: {
      MuiTooltip: {
        ...theme.components.MuiTooltip,
        styleOverrides: {
          tooltip: {
            backgroundColor: `rgba(0, 0, 0, 0.87)`,
            color: colorsToUse.PrimaryContrast,
          },
        },
      },
      MuiTabs: {
        root: {
          fontFamily: "Neue Haas Grotesk Display Pro",
          fontSize: '16px',
        }
      },
      MuiTab: {
        root: {
          fontFamily: "Neue Haas Grotesk Display Pro",
          fontSize: '16px',
        }
      },
      MuiButton: {
        ...theme.components.MuiButton,
        defaultProps: {
          disableElevation: true,
          disableFocusRipple: true,
          disableRipple: true,
          disableTouchRipple: true,
        },
        variants: [
          {
            props: { variant: "containedInverse" },
            style: {
              ...theme.components?.MuiButton?.styleOverrides?.contained,
              color: colorsToUse.PrimaryMain,
              backgroundColor: colorsToUse.PrimaryContrast,
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "15px",
              fontFamily: "Neue Haas Grotesk Display Pro",
              padding: "16px 32px",
            },
          },
        ],
        styleOverrides: {
          ...theme.components?.MuiButton?.styleOverrides,
          fontFamily: "Neue Haas Grotesk Display Pro",
          root: {
            ...theme.components?.MuiButton?.styleOverrides?.root,
            color: colorsToUse.TextDark,
            borderRadius: "8px",
            fontFamily: "Neue Haas Grotesk Display Pro",
            fontWeight: 600,
            paddingLeft: "32px",
            paddingRight: "32px",
            fontSize: "20px",
            opacity: 1,
            transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
            "&:hover": {
              opacity: uiOpacityHover,
              color: colorsToUse.TextPrimary,
              textDecoration: "none",
            },
          },
          contained: {
            ...theme.components?.MuiButton?.styleOverrides?.contained,
            color: colorsToUse.PrimaryContrast,
            background: colorsToUse.PrimaryMain,
            borderRadius: "8px",
            fontFamily: "Neue Haas Grotesk Display Pro",
            fontWeight: 600,
            paddingLeft: "32px",
            paddingRight: "32px",
            "&:hover": {
              color: colorsToUse.PrimaryContrast,
              background: colorsToUse.PrimaryMain,
            }
          },
          outlined: {
            ...theme.components?.MuiButton?.styleOverrides?.outlined,
            color: colorsToUse.TextPrimary,
            backgroundColor: "transparent",
            boxShadow: `inset 0px 0px 0px 1px ${colorsToUse.TextPrimary}`,
            borderRadius: "8px",
            fontFamily: "Neue Haas Grotesk Display Pro",
            fontWeight: 600,
            paddingLeft: "32px",
            paddingRight: "32px",
            "&:hover": {
              boxShadow: `inset 0px 0px 0px 1px ${colorsToUse.TextPrimary}`,
            },
          },
        },
      },
      MuiLink: {
        ...theme.components.MuiLink,
        styleOverrides: {
          root: {
            color: colorsToUse.TextPrimary,
            fontFamily: "Neue Haas Grotesk Display Pro",
            opacity: 1,
            transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
            textDecoration: "none",
            "&:hover": {
              opacity: uiOpacityHover,
              color: colorsToUse.TextPrimary,
              textDecoration: "none",
            },
          },
        },
      },
      MuiTypography: {
        ...theme.components.MuiTypography,
        styleOverrides: {
          ...theme.components.MuiTypography.styleOverrides,
          h1: {
            ...theme.components.MuiTypography.h1,
            fontFamily: "Crowdstrike Sharp Sans",
            fontWeight: 700,
            fontSize: "70px",
            lineHeight: "72px",
            [theme.breakpoints.down("md")]: {
              fontSize: "64px",
              lineHeight: "66px",
            },
            [theme.breakpoints.down("sm")]: {
              fontSize: "56px",
              lineHeight: "58px",
            },
            color: colorsToUse.TextPrimary,
          },
          h2: {
            ...theme.components.MuiTypography.h2,
            fontFamily: "Crowdstrike Sharp Sans",
            fontWeight: 700,
            lineHeight: "50px",
            fontSize: "48px",
            [theme.breakpoints.down("md")]: {
              fontSize: "42px",
              lineHeight: "44px",
            },
            [theme.breakpoints.down("sm")]: {
              fontSize: "38px",
              lineHeight: "40px",
            },
            color: colorsToUse.TextPrimary,
          },
          h3: {
            ...theme.components.MuiTypography.h3,
            fontFamily: "Crowdstrike Sharp Sans",
            fontWeight: 700,
            fontSize: "44px",
            lineHeight: "46px",
            [theme.breakpoints.down("md")]: {
              fontSize: "36px",
              lineHeight: "38px",
            },
            [theme.breakpoints.down("sm")]: {
              fontSize: "32px",
              lineHeight: "34px",
            },
            color: colorsToUse.TextPrimary,
          },
          h4: {
            ...theme.components.MuiTypography.h4,
            fontFamily: "Crowdstrike Sharp Sans",
            fontSize: "26px",
            lineHeight: "150%",
            color: colorsToUse.InfoLight,
            fontWeight: 700,
          },
          h5: {
            ...theme.components.MuiTypography.h5,
            fontFamily: "Crowdstrike Sharp Sans",
            lineHeight: "150%",
            fontWeight: 700,
            color: colorsToUse.TextPrimary,
          },
          h6: {
            ...theme.components.MuiTypography.h6,
            fontFamily: "Crowdstrike Sharp Sans",
            lineHeight: "150%",
            fontWeight: 700,
            color: colorsToUse.TextPrimary,
          },
          subtitle1: {
            ...theme.components.MuiTypography.subtitle1,
            fontFamily: "Crowdstrike Sharp Sans",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "1rem",
            lineHeight: "1.5rem",
          },
          subtitle2: {
            ...theme.components.MuiTypography.subtitle2,
            fontFamily: "Crowdstrike Sharp Sans",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "0.875rem",
            lineHeight: "1.375rem",
          },
          body1: {
            ...theme.components.MuiTypography.body1,
            fontFamily: "Neue Haas Grotesk Display Pro",
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: "20px",
            [theme.breakpoints.down("md")]: {
              fontSize: "16px",
            },
            lineHeight: "32px",
            color: colorsToUse.BodyText,
          },
          body2: {
            ...theme.components.MuiTypography.body2,
            fontFamily: "Neue Haas Grotesk Display Pro",
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: "18px",
            lineHeight: "150%",
            letterSpacing: 0,
            color: colorsToUse.BodyText,
          },
          caption: {
            ...theme.components.MuiTypography.caption,
            fontFamily: "Neue Haas Grotesk Display Pro",
            textTransform: "uppercase",
            color: colorsToUse.TextPrimary,
            fontSize: "12px",
            [theme.breakpoints.up("md")]: {
              fontSize: "14px",
            },
            fontWeight: 500,
            letterSpacing: 2.8,
          },
          button: {
            ...theme.components.MuiTypography.button,
            color: colorsToUse.PrimaryContrast,
            fontFamily: "Neue Haas Grotesk Display Pro",
            fontStyle: "normal",
            fontWeight: 600,
            fontSize: "16px",
            lineHeight: "200%",
            textTransform: "capitalize",
          },
          overline: {
            ...theme.components.MuiTypography.overline,
            fontFamily: "Neue Haas Grotesk Display Pro",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "0.75rem",
            lineHeight: "1.25rem",
            textTransform: "uppercase",
          },
        },
      },
    },
  });
};
